import type { SessionDataWithGraphQL, FolderHierarchyResult } from 'owa-graph-schema';
import type { MailboxInfo } from 'owa-client-types';
import type { PerformanceDatapoint } from 'owa-analytics';

import { addBottleneck } from 'owa-performance';
import { mapFindFolderResponseToGql } from 'owa-folder-gql-mappers';
import { shouldShowFolder } from 'owa-folders-data-utils';
import { setFoldersDataCollectionInformation } from './registerDataCollector';

export function extractFolderHierarchy(
    sessionData: SessionDataWithGraphQL | undefined,
    mailboxInfoInput: MailboxInfo,
    dp?: PerformanceDatapoint
): FolderHierarchyResult | undefined {
    const bottleneck = 'ffr';
    let result = sessionData?.folderHierarchy;
    let bottleneckReason = 's';
    // 'offlineBoot' when the offline-boot hierarchy is present; 'startupData' when mapped from findFolders.
    let source: string | undefined = result ? 'OFFLINE_BOOT' : undefined;
    const shouldShowNotesFolder = shouldShowFolder('notes', mailboxInfoInput);
    const shouldShowConversationHistoryFolder = shouldShowFolder(
        'conversationhistory',
        mailboxInfoInput
    );
    const shouldShowOutboxFolder = shouldShowFolder('outbox', mailboxInfoInput);
    let dataCollectorInformation: Error | string = '';
    if (!result) {
        const singleResponseMessage = sessionData?.findFolders?.Body?.ResponseMessages?.Items?.[0];
        if (!singleResponseMessage) {
            bottleneckReason = 'nrm';
            dataCollectorInformation = 'no response message';
        } else if (singleResponseMessage.ResponseClass == 'Error') {
            bottleneckReason = 'e';
            addBottleneck(bottleneck + '_c', singleResponseMessage.ResponseCode || 'un');
            dataCollectorInformation = 'response error';
        } else {
            try {
                result = mapFindFolderResponseToGql(
                    singleResponseMessage,
                    mailboxInfoInput,
                    shouldShowNotesFolder,
                    shouldShowConversationHistoryFolder,
                    shouldShowOutboxFolder
                );
                source = 'STARTUP_DATA';
                dataCollectorInformation = 'mapping succeeded';
            } catch (e) {
                if (e instanceof Error) {
                    bottleneckReason = 'm';
                    addBottleneck(bottleneck + '_c', e.message);
                    dataCollectorInformation = e;
                }
                source = undefined;
            }
        }
    } else {
        // When booting offline, the folder hierarchy is already available. At the point that is retrieved, the code does not
        // have the information to correctly determine if the following folders should be shown. So we need to filter them out here.
        if (result.Folders) {
            const filteredFolders = result.Folders.filter(folder => {
                const distinguishedFolderId = folder?.distinguishedFolderType;
                switch (distinguishedFolderId) {
                    case 'notes':
                        return shouldShowNotesFolder;
                    case 'conversationhistory':
                        return shouldShowConversationHistoryFolder;
                    case 'outbox':
                        return shouldShowOutboxFolder;
                    default:
                        return true;
                }
            });
            result.Folders = filteredFolders;
            dataCollectorInformation =
                'folder hierarchy already available with number of folders ' +
                filteredFolders.length;
        } else {
            dataCollectorInformation = 'folder hierarchy had no folders for primary mailbox';
            // Set result to undefined so that way getPrimaryFolders will call fetchPrimaryMailFolders to get folder list from service
            result = undefined;
            source = undefined;
        }
    }

    if (sessionData) {
        // If the sessionData was passed in, set the data collector information
        setFoldersDataCollectionInformation('extractFolderHierachy', dataCollectorInformation);
    } else {
        // If the sessionData was not passed in, set the data collector information to indicate that
        setFoldersDataCollectionInformation('sessionData', 'session data was not passed in');
    }
    // Log where the hierarchy came from (offline boot vs startup findFolders) on the caller's
    // datapoint. When session data had nothing usable, source is undefined and the caller logs the
    // service fetch source instead. No-op when no datapoint is provided.
    if (source !== undefined) {
        dp?.addCustomData({ source });
    }
    addBottleneck('ffr', bottleneckReason);
    return result;
}
